import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { candidateService } from '../services/candidateService';
import { projectService } from '../services/projectService';
import {
  CCard, CCardBody, CCardHeader,
  CRow, CCol,
  CTable, CTableHead, CTableBody, CTableRow, CTableHeaderCell, CTableDataCell,
  CBadge, CButton, CFormSelect, CFormInput,
  CModal, CModalHeader, CModalTitle, CModalBody,
  CTooltip, CSpinner,
} from '@coreui/react';
import CandidateFormDialog from '../components/CandidateFormDialog';
import { Users, Plus, Download, Search, CheckCircle, XCircle, UserCheck, Info } from 'lucide-react';

const MAPPING_LABELS = { new: 'New', screening: 'Screening', interview: 'Interview', offer: 'Offer', hired: 'Hired', rejected: 'Rejected', pool: 'Pool' };
const MAPPING_COLORS = { new: 'secondary', screening: 'info', interview: 'warning', offer: 'primary', hired: 'success', rejected: 'danger', pool: 'dark' };
const SCORE_LABELS = { english: 'ENG', s1_building_manager: 'S1', s2_village_manager: 'S2', s3_technician: 'S3', s4_head_technician: 'S4', s5_admin: 'S5', s6_rm: 'S6', s7: 'S7' };

export default function CandidatePoolPage() {
  const { authAxios, user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [positions, setPositions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [filterZone, setFilterZone] = useState('');
  const [filterMapping, setFilterMapping] = useState('');
  const [filterPass, setFilterPass] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editCandidate, setEditCandidate] = useState(null);
  const [detailCandidate, setDetailCandidate] = useState(null);

  const api = authAxios();
  const isHR = user?.role === 'hr';

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterPosition) params.position_id = filterPosition;
      if (filterZone) params.preferred_work_zone = filterZone;
      if (filterMapping) params.mapping_status = filterMapping;
      if (filterPass) params.pass_status = filterPass;
      const [candRes, posRes, projRes, zoneRes] = await Promise.all([
        candidateService.getAll(api, params),
        projectService.getPositions(api),
        projectService.getProjects(api),
        projectService.getWorkZones(api),
      ]);
      setCandidates(candRes.data);
      setPositions(posRes.data);
      setProjects(projRes.data);
      setZones(zoneRes.data);
    } catch { toast.error('Failed to load candidates'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [filterPosition, filterZone, filterMapping, filterPass]);

  const posMap = {}; positions.forEach(p => { posMap[p.id] = p.name; });
  const projMap = {}; projects.forEach(p => { projMap[p.id] = p.name; });

  const filtered = candidates.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = `${c.prefix} ${c.first_name} ${c.last_name} ${c.nickname}`.toLowerCase();
    return name.includes(q) || (c.email || '').toLowerCase().includes(q) || (c.phone || '').includes(q);
  });

  const handleTogglePass = async (c) => {
    try {
      await candidateService.togglePass(api, c.id, !c.pass_status?.passed);
      toast.success('Pass status updated');
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const handleSave = () => { setFormOpen(false); setEditCandidate(null); fetchData(); };

  const totalScore = (scores) => {
    if (!scores) return '-';
    const entries = Object.entries(scores).filter(([, v]) => v > 0);
    if (!entries.length) return '-';
    return `${entries.reduce((s, [, v]) => s + v, 0)}/${entries.length * 20}`;
  };

  const hasFilter = filterPosition || filterZone || filterMapping || filterPass;

  return (
    <div data-testid="candidate-pool-page">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1 d-flex align-items-center gap-2"><Users size={22} /> Candidate Pool</h4>
          <small className="text-body-secondary">
            {filtered.length} candidate{filtered.length !== 1 ? 's' : ''} &middot; {filtered.filter(c => c.pass_status?.passed).length} passed
          </small>
        </div>
        <div className="d-flex gap-2">
            <CButton color="light" size="sm" onClick={() => candidateService.exportCSV(api)} data-testid="export-candidates-btn">
              <Download size={14} className="me-1" /> Export CSV
            </CButton>
          {isHR && (
            <CButton color="primary" size="sm" onClick={() => { setEditCandidate(null); setFormOpen(true); }} data-testid="add-candidate-btn">
              <Plus size={14} className="me-1" /> Add Candidate
            </CButton>
          )}
        </div>
      </div>

      {/* Filters */}
      <CCard className="mb-4 border-0 shadow-sm">
        <CCardBody className="py-2">
          <CRow className="g-2 align-items-center">
            <CCol xs="auto"><Search size={14} className="text-body-secondary" /></CCol>
            <CCol sm={3} md={2}>
              <CFormInput size="sm" placeholder="Search name, email..." value={search} onChange={e => setSearch(e.target.value)} data-testid="candidate-search" />
            </CCol>
            <CCol sm={2}>
              <CFormSelect size="sm" value={filterPosition} onChange={e => setFilterPosition(e.target.value)} data-testid="filter-cand-position">
                <option value="">All Positions</option>
                {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </CFormSelect>
            </CCol>
            <CCol sm={2}>
              <CFormSelect size="sm" value={filterZone} onChange={e => setFilterZone(e.target.value)} data-testid="filter-cand-zone">
                <option value="">All Zones</option>
                {zones.map(z => <option key={z} value={z}>{z}</option>)}
              </CFormSelect>
            </CCol>
            <CCol sm={2}>
              <CFormSelect size="sm" value={filterMapping} onChange={e => setFilterMapping(e.target.value)} data-testid="filter-cand-mapping">
                <option value="">All Status</option>
                {Object.entries(MAPPING_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </CFormSelect>
            </CCol>
            <CCol sm={2}>
              <CFormSelect size="sm" value={filterPass} onChange={e => setFilterPass(e.target.value)} data-testid="filter-cand-pass">
                <option value="">Pass Status</option>
                <option value="passed">Passed</option>
                <option value="not_passed">Not Passed</option>
              </CFormSelect>
            </CCol>
            {hasFilter && (
              <CCol xs="auto">
                <CButton color="ghost-secondary" size="sm" onClick={() => { setFilterPosition(''); setFilterZone(''); setFilterMapping(''); setFilterPass(''); }} data-testid="clear-cand-filters">Clear</CButton>
              </CCol>
            )}
          </CRow>
        </CCardBody>
      </CCard>

      {/* Table */}
      <CCard className="border-0 shadow-sm" data-testid="candidates-table">
        <CCardBody className="p-0">
          {loading ? (
            <div className="text-center py-5"><CSpinner color="primary" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-5 text-body-secondary">No candidates found</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <CTable hover small className="mb-0">
                <CTableHead color="light">
                  <CTableRow>
                    <CTableHeaderCell className="small text-body-secondary text-uppercase">Name</CTableHeaderCell>
                    <CTableHeaderCell className="small text-body-secondary text-uppercase">Position</CTableHeaderCell>
                    <CTableHeaderCell className="small text-body-secondary text-uppercase">Zone</CTableHeaderCell>
                    <CTableHeaderCell className="small text-body-secondary text-uppercase">Status</CTableHeaderCell>
                    <CTableHeaderCell className="small text-body-secondary text-uppercase">Project</CTableHeaderCell>
                    <CTableHeaderCell className="small text-body-secondary text-uppercase text-center">Score</CTableHeaderCell>
                    <CTableHeaderCell className="small text-body-secondary text-uppercase text-center">Passed</CTableHeaderCell>
                    <CTableHeaderCell className="small text-body-secondary text-uppercase text-center">Actions</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {filtered.map(c => {
                    const ps = c.pass_status || {};
                    const canToggle = isHR || user?.role === 'rm' || user?.role === 'director';
                    return (
                      <CTableRow key={c.id}>
                        <CTableDataCell>
                          <div className="small fw-medium">{c.prefix} {c.first_name} {c.last_name}</div>
                          <div className="small text-body-secondary">{c.nickname ? `(${c.nickname})` : ''} {c.phone}</div>
                        </CTableDataCell>
                        <CTableDataCell className="small">{posMap[c.position_id] || '-'}</CTableDataCell>
                        <CTableDataCell className="small">{c.preferred_work_zone || '-'}</CTableDataCell>
                        <CTableDataCell>
                          <CBadge color={MAPPING_COLORS[c.mapping_status] || 'secondary'} shape="rounded-pill" className="small">
                            {MAPPING_LABELS[c.mapping_status] || c.mapping_status}
                          </CBadge>
                        </CTableDataCell>
                        <CTableDataCell className="small">{projMap[c.project_id] || '-'}</CTableDataCell>
                        <CTableDataCell className="small text-center font-monospace">{totalScore(c.scores)}</CTableDataCell>
                        <CTableDataCell className="text-center">
                          <CTooltip content={
                            ps.method === 'system' ? 'Auto-checked by system (S1-S7 >= 80%)' :
                            ps.method === 'manual' ? `Manual by ${ps.checked_by_name} (${ps.checked_by_role})` : 'Not checked'
                          }>
                            <CBadge
                              color={ps.passed ? 'success' : 'secondary'}
                              shape="rounded-pill"
                              role={canToggle ? 'button' : undefined}
                              onClick={() => canToggle && handleTogglePass(c)}
                              data-testid={`pass-toggle-${c.id}`}
                              className="d-inline-flex align-items-center gap-1"
                            >
                              {ps.passed ? <CheckCircle size={12} /> : <XCircle size={12} />}
                              {ps.passed ? 'Yes' : 'No'}
                            </CBadge>
                          </CTooltip>
                        </CTableDataCell>
                        <CTableDataCell className="text-center">
                          <CButton color="ghost-secondary" size="sm" className="p-1" onClick={() => setDetailCandidate(c)} data-testid={`view-cand-${c.id}`}>
                            <Info size={14} />
                          </CButton>
                          {isHR && (
                            <CButton color="ghost-secondary" size="sm" className="p-1" onClick={() => { setEditCandidate(c); setFormOpen(true); }} data-testid={`edit-cand-${c.id}`}>
                              <UserCheck size={14} />
                            </CButton>
                          )}
                        </CTableDataCell>
                      </CTableRow>
                    );
                  })}
                </CTableBody>
              </CTable>
            </div>
          )}
        </CCardBody>
      </CCard>

      {/* Form Dialog */}
      <CandidateFormDialog open={formOpen} onClose={() => { setFormOpen(false); setEditCandidate(null); }}
        onSave={handleSave} candidate={editCandidate} positions={positions} projects={projects} />

      {/* Detail Modal */}
      <CModal visible={!!detailCandidate} onClose={() => setDetailCandidate(null)} size="lg" data-testid="candidate-detail-dialog">
        <CModalHeader><CModalTitle className="fw-bold">{detailCandidate?.prefix} {detailCandidate?.first_name} {detailCandidate?.last_name}</CModalTitle></CModalHeader>
        <CModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {detailCandidate && (
            <div>
              <CRow className="g-2 mb-3">
                {[
                  ['Age', detailCandidate.age], ['Phone', detailCandidate.phone], ['Email', detailCandidate.email],
                  ['Education', detailCandidate.education_level], ['Address', detailCandidate.current_address],
                  ['Work Zone', detailCandidate.preferred_work_zone], ['Last Salary', detailCandidate.last_salary?.toLocaleString()],
                  ['Expected', detailCandidate.expected_salary?.toLocaleString()], ['Available', detailCandidate.available_start_date],
                  ['Experience', detailCandidate.building_management_experience], ['Round', detailCandidate.interview_round],
                  ['Channel', detailCandidate.job_posting_channel], ['Mapping', MAPPING_LABELS[detailCandidate.mapping_status]],
                  ['Project', projMap[detailCandidate.project_id]], ['Offer', detailCandidate.offer_status],
                  ['Interview', detailCandidate.interview_result],
                ].map(([label, val], i) => (
                  <CCol sm={6} key={i}><small className="text-body-secondary">{label}:</small> <span className="small fw-medium">{val || '-'}</span></CCol>
                ))}
              </CRow>
              <h6 className="fw-semibold mb-2">Scores (out of 20)</h6>
              <CRow className="g-2 mb-3">
                {Object.entries(SCORE_LABELS).map(([key, label]) => {
                  const val = detailCandidate.scores?.[key] || 0;
                  const isS = key !== 'english';
                  const passed = isS && val >= 16;
                  return (
                    <CCol xs={3} key={key}>
                      <div className={`text-center p-2 rounded border ${val > 0 ? (isS ? (passed ? 'border-success bg-success bg-opacity-10' : 'border-danger bg-danger bg-opacity-10') : 'border-primary bg-primary bg-opacity-10') : ''}`}>
                        <div className="small text-body-secondary">{label}</div>
                        <div className="fs-5 fw-bold">{val || '-'}</div>
                      </div>
                    </CCol>
                  );
                })}
              </CRow>
              <div className={`d-flex align-items-center gap-2 p-3 rounded ${detailCandidate.pass_status?.passed ? 'bg-success bg-opacity-10' : 'bg-body-secondary bg-opacity-10'}`}>
                {detailCandidate.pass_status?.passed ? <CheckCircle size={18} className="text-success" /> : <XCircle size={18} className="text-body-secondary" />}
                <div>
                  <div className="small fw-semibold">{detailCandidate.pass_status?.passed ? 'Passed' : 'Not Passed'}</div>
                  <div className="small text-body-secondary">
                    {detailCandidate.pass_status?.method === 'system' ? 'Auto-checked by system' :
                     detailCandidate.pass_status?.method === 'manual' ? `Manual by ${detailCandidate.pass_status.checked_by_name} (${detailCandidate.pass_status.checked_by_role})` : 'Not evaluated'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </CModalBody>
      </CModal>
    </div>
  );
}
